import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useSeason } from "@/lib/SeasonContext";
import { Button } from "@/components/ui/button";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import PlayerEditDialog from "@/components/app/PlayerEditDialog";
import { Upload, Plus, Pencil, Archive, KeyRound, RotateCw, ScanLine, History, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Roster() {
  const { activeSeason, seasons } = useSeason();
  const isHistorical = activeSeason && seasons.length > 0 && seasons[0] !== activeSeason;
  const [players, setPlayers] = useState([]);
  const [statsByPlayer, setStatsByPlayer] = useState({});
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const params = { status_filter: "active" };
    if (isHistorical) {
      params.season = activeSeason;
      params.include_active = true;
      params.status_filter = "active,graduated,inactive"; // historical seasons include everyone in that season
    }
    const [p, s] = await Promise.all([
      api.get("/players", { params }),
      api.get("/game-stats"),
    ]);
    setPlayers(p.data);
    const map = {};
    for (const st of s.data) {
      (map[st.player_id] = map[st.player_id] || []).push(st);
    }
    setStatsByPlayer(map);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeSeason, isHistorical]);

  const archive = async (p) => {
    if (!confirm(`Archive ${p.player_name}? Stats are preserved.`)) return;
    try {
      await api.post(`/players/${p.id}/archive`);
      toast.success(`Archived ${p.player_name}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/players/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Roster: ${data.added} added, ${data.updated} updated`);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { e.target.value = ""; }
  };

  const copyCode = async (p) => {
    if (!p.photo_upload_code) { toast.error("No code available"); return; }
    try {
      await navigator.clipboard.writeText(p.photo_upload_code);
      toast.success(`Copied code for ${p.player_name}: ${p.photo_upload_code}`);
    } catch { toast.error("Copy failed"); }
  };

  const rotateCode = async (p) => {
    if (!confirm(`Generate a new photo upload code for ${p.player_name}? The old code will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/players/${p.id}/rotate-photo-code`);
      toast.success(`New code for ${p.player_name}: ${data.photo_upload_code}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const ppgOf = (p) => {
    const ss = statsByPlayer[p.id] || [];
    if (!ss.length) return 0;
    const tot = ss.reduce((acc, s) => acc + 2 * (s.fg2_made || 0) + 3 * (s.fg3_made || 0) + (s.ft_made || 0), 0);
    return (tot / ss.length).toFixed(1);
  };

  const purgeTestPlayers = async () => {
    try {
      const { data: preview } = await api.get("/roster/preview-test-purge");
      if (preview.length === 0) {
        toast.info("No test players found in the roster");
        return;
      }
      const names = preview.map((p) => `• ${p.player_name}${p.status !== "active" ? ` (${p.status})` : ""}`).join("\n");
      const ok = confirm(`Permanently delete ${preview.length} test player${preview.length === 1 ? "" : "s"}?\n\n${names}\n\nThis also removes their stats, videos, and attendance. Can't be undone.`);
      if (!ok) return;
      const { data } = await api.post("/roster/purge-test");
      toast.success(`Removed ${data.deleted} test player${data.deleted === 1 ? "" : "s"}: ${data.names.join(", ")}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="eyebrow">Active Roster</p>
          <h1 className="heading-1">Roster <span className="text-burgundy">Management</span></h1>
          {isHistorical && (
            <div className="mt-3 inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 px-3 py-1.5 rounded-md text-xs uppercase tracking-wider" data-testid="roster-historical-banner">
              <History className="w-3.5 h-3.5" />
              Historical season <span className="font-bold text-white ml-1">{activeSeason}</span> — showing this season&apos;s alumni + current active players
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/access-cards"
            className="inline-flex items-center gap-2 border border-ink-700 hover:bg-ink-800 text-ink-200 hover:text-white px-3 py-2 rounded-md text-sm transition"
            data-testid="roster-access-cards-link"
          >
            <ScanLine className="w-4 h-4" /> Access Cards
          </Link>
          <Button
            variant="outline"
            className="border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200"
            onClick={purgeTestPlayers}
            title="Removes any players whose name looks like a testing artifact (starts with TEST or is 'Hacker')."
            data-testid="roster-purge-test-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Cleanup Test Players
          </Button>
          <Button variant="outline" className="border-ink-700 hover:bg-ink-800" onClick={() => fileRef.current?.click()} data-testid="roster-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> Upload
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={handleUpload} />
          <Button className="bg-burgundy hover:bg-burgundy-hover" onClick={() => setAdding(true)} data-testid="roster-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Player
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {players.map((p) => (
          <div key={p.id} className="bg-ink-800 rounded-lg border border-white/5 p-4 hover:border-burgundy/40 transition" data-testid={`roster-card-${p.slug}`}>
            <div className="flex items-center gap-4">
              <PlayerAvatar player={p} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-bold uppercase leading-tight truncate">{p.player_name}</p>
                <p className="text-ink-400 text-xs">#{p.jersey_number || "—"} · {p.position || "—"} · {p.grade || "—"}</p>
                <div className="mt-2 flex items-center gap-4 text-sm">
                  <span><span className="text-ink-400 text-xs uppercase">PPG </span><span className="font-display font-bold">{ppgOf(p)}</span></span>
                  <span className="px-2 py-0.5 bg-green-500/15 text-green-400 text-[10px] uppercase rounded">Active</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="flex-1 border-ink-700 hover:bg-ink-700" onClick={() => setEditing(p)} data-testid={`roster-edit-${p.slug}`}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="flex-1 border-ink-700 hover:bg-ink-700" onClick={() => archive(p)} data-testid={`roster-archive-${p.slug}`}>
                <Archive className="w-3 h-3 mr-1" /> Archive
              </Button>
            </div>
            {p.photo_upload_code && (
              <div className="flex gap-2 mt-2 pt-2 border-t border-ink-700/30">
                <button
                  onClick={() => copyCode(p)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded text-[10px] uppercase tracking-wider font-bold text-ink-300 hover:text-white hover:bg-ink-700 transition"
                  title="Copy this player's upload code — share it so they can update their photo"
                  data-testid={`roster-copycode-${p.slug}`}
                >
                  <KeyRound className="w-3 h-3" />
                  <span className="font-mono">{p.photo_upload_code}</span>
                </button>
                <button
                  onClick={() => rotateCode(p)}
                  className="px-2 py-1.5 rounded text-ink-400 hover:text-burgundy hover:bg-ink-700 transition"
                  title="Rotate this code (invalidates the old one)"
                  data-testid={`roster-rotatecode-${p.slug}`}
                >
                  <RotateCw className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <PlayerEditDialog
        open={!!editing || adding}
        player={editing}
        onClose={() => { setEditing(null); setAdding(false); }}
        onSaved={() => { setEditing(null); setAdding(false); load(); }}
      />
    </div>
  );
}
